import { useState, useEffect, type ComponentProps } from 'react';

import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { Popover } from '../../components/ui/Popover';
import { Tag } from '../../components/ui/Tag';
import { Input } from '../../components/ui/Input';
import { Link } from '../../components/ui/Link';
import { ModuleSidebar } from '../../components/ui/ModuleSidebar';
import { PageBreadcrumb, PageContentCard } from '../../components/ui/ModulePage';
import { ProductNavigation } from '../../components/ui/ProductNavigation';
import { findProductModule } from '../../components/ui/ProductNavigation/navigation-data';
import demoQr from './assets/demo-qr.png';
import { buildPosterCanvas, type PosterKind } from './poster-generator';
import styles from './Page.module.css';

const frontDesk = findProductModule('front-desk');

function AmountTrigger(props: ComponentProps<typeof Link>) {
  return <Link {...props} type="primary" />;
}

export function ChargePage() {
  const [chargeOpen, setChargeOpen] = useState(false);
  const [amount, setAmount] = useState<number | null>(null);
  const [draftAmount, setDraftAmount] = useState('');
  const draftAmountValue = Number(draftAmount);
  const validDraftAmount = Number.isFinite(draftAmountValue) && draftAmountValue >= 0.01 && draftAmountValue <= 999999.99;
  const [amountOpen, setAmountOpen] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);
  const [posterKind, setPosterKind] = useState<PosterKind>('personal');
  const posterTitle = posterKind === 'institution' ? '机构海报' : '个人海报';
  const [posterDataUrl, setPosterDataUrl] = useState<string>('');
  const [posterLoading, setPosterLoading] = useState(false);

  useEffect(() => {
    if (!posterOpen) return;
    let cancelled = false;
    buildPosterCanvas({
      amount,
      kind: posterKind,
      qrImageSrc: demoQr,
      campusName: '快乐学教育 · 岳麓校区',
    })
      .then((canvas) => {
        if (!cancelled) {
          setPosterDataUrl(canvas.toDataURL('image/png'));
          setPosterLoading(false);
        }
      })
      .catch((err) => {
        console.error('Failed to generate poster:', err);
        if (!cancelled) setPosterLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [posterOpen, amount, posterKind]);

  function openPoster(kind: PosterKind) {
    setPosterKind(kind);
    setPosterLoading(true);
    setPosterDataUrl('');
    setPosterOpen(true);
  }

  function handleAmountOpenChange(open: boolean) {
    if (open) setDraftAmount(amount === null ? '' : String(amount));
    setAmountOpen(open);
  }

  function confirmAmount() {
    if (!validDraftAmount) return;
    setAmount(draftAmountValue);
    setAmountOpen(false);
  }

  return <main className={styles.page}>
    <ProductNavigation defaultActive="前台业务" />
    <div className={styles.workspace}>
      <ModuleSidebar activeItem="收费" ariaLabel="前台业务导航" groups={frontDesk?.groups ?? []} />
      <section aria-label="收费页面" className={styles.main}>
        <h1 className={styles.visuallyHidden}>收费</h1>
        <PageBreadcrumb current="收费" items={['前台业务']} />
        <PageContentCard className={styles.contentCard}>
          <div aria-label="收费操作" className={styles.toolbar} role="toolbar">
            <Button aria-haspopup="dialog" className={styles.chargeTrigger} leadingIcon="qr-code-line" onClick={() => setChargeOpen(true)} type="default">快速收费</Button>
          </div>
        </PageContentCard>
      </section>
    </div>
    <Dialog
      className={styles.chargeDialog}
      description={<Tag size="small" type="success">长期有效</Tag>}
      footer={<>
        <Button onClick={() => openPoster('institution')} type="default">生成机构海报</Button>
        <Button onClick={() => openPoster('personal')}>生成个人海报</Button>
      </>}
      onOpenChange={(open) => { setChargeOpen(open); if (!open) setAmountOpen(false); }}
      open={chargeOpen}
      title="快速收费"
      width={560}
    >
      <section aria-label="静态二维码收款" className={styles.chargeBody}>
        <section aria-label="收款二维码" className={styles.preview}>
          <div className={styles.previewHeader}>
            <h2 className={amount === null ? undefined : styles.fixedAmount}>{amount === null ? '扫码缴费' : `¥${amount.toFixed(2)}`}</h2>
            <div className={styles.amountControls}>
              <Popover
                align="center"
                content={<div className={styles.amountEditor}>
                  <Input ariaLabel="缴费金额" className={styles.amountInput} id="quick-charge-amount" inputMode="decimal" onPressEnter={confirmAmount} onValueChange={(value) => { if (/^\d{0,6}(?:\.\d{0,2})?$/.test(value)) setDraftAmount(value); }} placeholder="0.00" prefix="¥" value={draftAmount} />
                  <div className={styles.amountActions}>
                    <Button onClick={() => setAmountOpen(false)} size="small" type="default">取消</Button>
                    <Button disabled={!validDraftAmount} onClick={confirmAmount} size="small">确定</Button>
                  </div>
                </div>}
                onOpenChange={handleAmountOpenChange}
                open={amountOpen}
                side="top"
                title="设置缴费金额"
              >
                <AmountTrigger className={styles.amountLink} id="quick-charge-amount-trigger">{amount === null ? '设置金额' : '修改'}</AmountTrigger>
              </Popover>
              {amount !== null ? <Link onClick={() => {
                setAmount(null);
                setAmountOpen(false);
                document.getElementById('quick-charge-amount-trigger')?.focus();
              }}>清除</Link> : null}
            </div>
          </div>
          <div className={styles.qrFrame}><img alt="收款二维码示意码，无真实收款能力" className={styles.qrImage} src={demoQr} /></div>
          <p className={styles.association}>使用微信扫码，支付后自动关联缴费学员</p>
        </section>
      </section>
      <Dialog
        className={styles.posterDialog}
        modalLayer="nested"
        description={posterKind === 'personal' ? '收款流水的经手人记录为当前老师（张老师）' : '收款流水中不记录经手老师'}
        footer={<>
          <span className={styles.posterMode}>{amount !== null ? '含固定金额' : '家长自填金额'}</span>
          <Button onClick={() => setPosterOpen(false)} type="default">取消</Button>
          <Button
            disabled={posterLoading || !posterDataUrl}
            leadingIcon="download-line"
            onClick={() => {
              if (!posterDataUrl) return;
              const anchor = document.createElement('a');
              anchor.href = posterDataUrl;
              anchor.download = `${posterTitle}_${amount !== null ? `${amount.toFixed(2)}元` : '未设置金额'}.png`;
              anchor.click();
              setPosterOpen(false);
            }}
          >
            保存图片
          </Button>
        </>}
        onOpenChange={setPosterOpen}
        open={posterOpen}
        title={posterTitle}
        width={480}
      >
        <div className={styles.posterPreview}>
          {posterLoading ? (
            <div className={styles.posterLoading}>正在生成海报...</div>
          ) : (
            <img
              alt={`待下载的${posterTitle}`}
              className={styles.posterImage}
              src={posterDataUrl}
            />
          )}
        </div>
      </Dialog>
    </Dialog>
  </main>;
}
